import React, { useState, useEffect, memo } from 'react'
import TableOperationsUI from './TableOperationsUI'
import {
  filterCustomCellRows,
  searchCustomCellRows,
  sortCustomCellRows,
} from './helperFunctions'
import { FilterCheckbox } from './FilterModel'
import { find } from 'lodash-es'

interface TableOperationsProps {
  rows: any[]
  setRows: (rows: any[]) => void
  filters: {
    header: string
    checkboxes: FilterCheckbox[]
  }[]
  searchColumnNumbers: number[]
  sortColumnNumber: number
  setNoCommutersTableMessage: () => void
  trailingActions?: React.ReactNode
}

const TableOperations = memo(
  ({
    rows,
    setRows,
    filters,
    searchColumnNumbers,
    sortColumnNumber,
    setNoCommutersTableMessage,
    trailingActions,
  }: TableOperationsProps) => {
    // Local state for displayed rows and filters/sort
    const [filterSet, setFilterSet] = useState(false)
    const [searchSet, setSearchSet] = useState(false)
    const [searchManipulatedRows, setSearchManipulatedRows] = useState<any[]>(
      []
    )
    const [filterManipulatedRows, setFilterManipulatedRows] = useState<any[]>(
      []
    )
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
    const [searchValue, setSearchValue] = useState('')
    const [isFilterButtonActive, setIsFilterButtonActive] = useState(false)
    const [isSearchActive, setIsSearchActive] = useState(false)

    // Get the final rows to display
    // getting the common rows between search, filter, and sort
    const finalRows = () => {
      // If nothing is set, return all rows
      if (!searchSet && !filterSet && !sortDirection) {
        return rows
      }
      // If only sort is set, return sorted rows
      if (!searchSet && !filterSet && sortDirection) {
        return sortCustomCellRows(rows, sortColumnNumber, sortDirection)
      }
      const commonRows = []
      // If any row is empty the result will be empty
      if (searchSet && searchManipulatedRows.length === 0) {
        return []
      }
      if (filterSet && filterManipulatedRows.length === 0) {
        return []
      }
      if (searchManipulatedRows.length)
        searchManipulatedRows.forEach((row) => {
          if (filterManipulatedRows.length) {
            if (
              find(filterManipulatedRows, { id: row.id }) &&
              !find(commonRows, { id: row.id })
            )
              commonRows.push(row)
          } else {
            commonRows.push(row)
          }
        })
      if (filterManipulatedRows.length)
        filterManipulatedRows.forEach((row) => {
          if (searchManipulatedRows.length) {
            if (
              find(searchManipulatedRows, { id: row.id }) &&
              !find(commonRows, { id: row.id })
            )
              commonRows.push(row)
          } else {
            commonRows.push(row)
          }
        })
      return sortCustomCellRows(commonRows, sortColumnNumber, sortDirection)
    }

    // Whenever original data changes, reset rows
    useEffect(() => {
      setSearchManipulatedRows([])
      setFilterManipulatedRows([])
      setSortDirection('desc')
      setSearchSet(false)
      setFilterSet(false)
    }, [rows])

    // Push up the current rows whenever they change
    useEffect(() => {
      setRows(finalRows())
    }, [searchManipulatedRows, filterManipulatedRows, sortDirection])

    // Handlers
    const handleApplyFilters = (filters: FilterCheckbox[]) => {
      const filtered = filterCustomCellRows(rows, filters)
      if (filtered.length === 0) {
        setNoCommutersTableMessage()
      }
      setFilterManipulatedRows(filtered)
      setFilterSet(true)
      setIsFilterButtonActive(true)
    }

    const handleFiltersReset = () => {
      setFilterManipulatedRows([])
      setFilterSet(false)
      setIsFilterButtonActive(false)
    }

    const handleSearch = (value: string) => {
      if (value) {
        const searched = searchCustomCellRows(rows, searchColumnNumbers, value)
        if (searched.length === 0) {
          setNoCommutersTableMessage()
        }
        setSearchManipulatedRows(searched)
        setSearchSet(true)
        setSearchValue(value)
        setIsSearchActive(true)
      } else {
        setSearchManipulatedRows([])
        setSearchSet(false)
        setSearchValue('')
        setIsSearchActive(false)
      }
    }

    const handleSearchClear = () => {
      setSearchManipulatedRows([])
      setSearchSet(false)
      setSearchValue('')
      setIsSearchActive(false)
    }

    const handleSortAsc = () => {
      setSortDirection('asc')
    }

    const handleSortDesc = () => {
      setSortDirection('desc')
    }

    return (
      <TableOperationsUI
        handleSearch={handleSearch}
        handleSearchClear={handleSearchClear}
        onAscendingClick={handleSortAsc}
        onDescendingClick={handleSortDesc}
        filters={filters}
        handleApplyFilters={handleApplyFilters}
        handleFiltersReset={handleFiltersReset}
        isFilterButtonActive={isFilterButtonActive}
        activeSortDirection={sortDirection}
        isSearchActive={isSearchActive}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        trailingActions={trailingActions}
      />
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  TableOperations.displayName = 'TableOperations'
}

export default TableOperations
